import { Button, Card, CardContent, FormField, Input, Label, PinPad, SelectField } from "@jod/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { LockKeyhole } from "lucide-react";
import type { FormEvent } from "react";
import { useState } from "react";
import { signInStaff } from "../lib/auth-session.ts";
import { env } from "../lib/env.ts";

export const Route = createFileRoute("/login")({ component: LoginScreen });

const branches = [{ value: "branch-thonglor", label: "Thonglor · ทองหล่อ" }];
const quick = [
  { code: "FLOOR", label: "Dev demo: Floor manager" },
  { code: "BAR", label: "Dev demo: Bar + runner" },
  { code: "CASH", label: "Dev demo: Cashier" },
  { code: "MOD", label: "Dev demo: Moderator" },
];

function LoginScreen() {
  const navigate = useNavigate();
  const [branchId, setBranchId] = useState(branches[0]?.value ?? "");
  const [staffCode, setStaffCode] = useState("FLOOR");
  const [pin, setPin] = useState("1234");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(event?: FormEvent<HTMLFormElement>): Promise<void> {
    event?.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const principal = await signInStaff(staffCode, pin);
      // The mock demo store uses placeholder branch ids ("branch-thonglor")
      // that only make sense against local mock data; the real backend
      // resolves branch scope server-side from the staff record (FR-STAFF-003)
      // so there is nothing meaningful to compare a real principal against.
      if (!principal || (env.apiMock && principal.branchId !== branchId)) {
        setError("Sign in failed. Check your staff code and PIN.");
        return;
      }
      void navigate({ to: "/today" });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-6 px-6 py-10">
      <div className="space-y-2 text-center">
        <span className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-secondary/15 text-secondary">
          <LockKeyhole className="size-7" />
        </span>
        <h1 className="font-display font-semibold text-2xl">Staff sign in</h1>
        <p className="text-muted-foreground text-sm">Just One Drink · Staff Ops · เข้าระบบพนักงาน</p>
      </div>
      <Card>
        <CardContent className="space-y-4 p-5">
          <form className="space-y-4" onSubmit={submit}>
            <SelectField
              id="branch"
              label="Branch / สาขา"
              value={branchId}
              onChange={setBranchId}
              options={branches}
            />
            <FormField id="staffCode" label="Staff code / รหัสพนักงาน">
              <Input
                id="staffCode"
                value={staffCode}
                autoComplete="username"
                onChange={(event) => setStaffCode(event.target.value.toUpperCase())}
              />
            </FormField>
            <FormField id="pin" label="PIN">
              <Input
                id="pin"
                value={pin}
                type="password"
                inputMode="numeric"
                autoComplete="current-password"
                onChange={(event) => setPin(event.target.value)}
              />
            </FormField>
            <PinPad
              onDigit={(digit) => setPin((value) => `${value}${digit}`.slice(0, 8))}
              onBackspace={() => setPin((value) => value.slice(0, -1))}
              onClear={() => setPin("")}
            />
            {error ? (
              <p role="alert" className="rounded-md bg-destructive/10 p-3 text-destructive text-sm">
                {error}
              </p>
            ) : null}
            <Button type="submit" className="min-h-12 w-full" disabled={submitting}>
              {submitting ? "Signing in… / กำลังเข้าระบบ" : "Sign in / เข้าระบบ"}
            </Button>
          </form>
          {env.apiMock ? (
            <div className="space-y-2 border-border border-t pt-4">
              <Label>Mock quick-login (dev/demo only)</Label>
              <div className="grid gap-2 sm:grid-cols-2">
                {quick.map((item) => (
                  <Button
                    key={item.code}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setStaffCode(item.code);
                      setPin("1234");
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
